import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API, useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { 
  Package, 
  Search, 
  RefreshCw, 
  Star, 
  MapPin, 
  Phone, 
  Ban,
  CheckCircle,
  AlertTriangle,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  Layers
} from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

export const FoodDriversManagement = () => {
  const { user } = useAuth();
  const [drivers, setDrivers] = useState([]);
  const [cities, setCities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchInput, setSearchInput] = useState(""); // For debounced search
  const [statusFilter, setStatusFilter] = useState("all");
  const [cityFilter, setCityFilter] = useState("all");
  const [showBlockDialog, setShowBlockDialog] = useState(false);
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [blockAction, setBlockAction] = useState("block");
  const [showMaxOrdersDialog, setShowMaxOrdersDialog] = useState(false);
  const [maxOrdersValue, setMaxOrdersValue] = useState("");
  
  // Pagination state
  const [page, setPage] = useState(1);
  const [limit] = useState(20);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  useEffect(() => {
    if (user?.id) {
      fetchCities();
    }
  }, [user?.id]);

  // Fetch drivers when filters change
  useEffect(() => {
    if (user?.id) {
      fetchFoodDrivers();
    }
  }, [user?.id, page, searchTerm, statusFilter, cityFilter]);

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchTerm(searchInput);
      setPage(1); // Reset to first page on search
    }, 500);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const fetchFoodDrivers = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
        food_delivery_only: "true"
      });
      
      if (searchTerm) params.append("search", searchTerm);
      if (statusFilter && statusFilter !== "all") params.append("status", statusFilter);
      if (cityFilter && cityFilter !== "all") params.append("city_id", cityFilter);
      
      const res = await axios.get(`${API}/admin/${user.id}/drivers/paginated?${params.toString()}`);
      
      setDrivers(res.data.drivers || []);
      setTotal(res.data.total || 0);
      setTotalPages(res.data.total_pages || 0);
    } catch (err) {
      toast.error("Failed to load food delivery drivers");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchCities = async () => {
    try {
      const res = await axios.get(`${API}/cities?admin_id=${user.id}`);
      const sortedCities = (res.data || []).filter(c => c.is_active).sort((a, b) => (a.name || '').localeCompare(b.name || ''));
      setCities(sortedCities);
    } catch (err) {
      console.error("Failed to load cities", err);
    }
  };

  const toggleFoodDelivery = async (driver, enabled) => {
    try {
      // Pass is_admin_update=true so backend knows this is admin approval
      await axios.put(`${API}/auth/driver/${driver.id}/service-types?is_admin_update=true`, {
        food_delivery: enabled
      });
      toast.success(enabled 
        ? `${driver.name} can now accept food delivery orders` 
        : `${driver.name} has been blocked from food delivery`
      );
      fetchFoodDrivers();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update service types");
    }
  };

  const updateMaxParallelOrders = async () => {
    if (!selectedDriver) return;
    try {
      const maxOrders = maxOrdersValue === "" || maxOrdersValue === "default" ? null : parseInt(maxOrdersValue);
      await axios.put(`${API}/auth/driver/${selectedDriver.id}/max-parallel-orders?max_orders=${maxOrders === null ? '' : maxOrders}`);
      toast.success(`Max parallel orders updated for ${selectedDriver.name}`);
      setShowMaxOrdersDialog(false);
      setSelectedDriver(null);
      setMaxOrdersValue("");
      fetchFoodDrivers();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update max parallel orders");
    }
  };

  const openMaxOrdersDialog = (driver) => {
    setSelectedDriver(driver);
    setMaxOrdersValue(driver.max_parallel_food_orders?.toString() || "");
    setShowMaxOrdersDialog(true);
  };

  const openBlockDialog = (driver, action) => {
    setSelectedDriver(driver);
    setBlockAction(action);
    setShowBlockDialog(true);
  };

  const handleConfirmBlock = async () => {
    if (!selectedDriver) return;
    
    const enabled = blockAction === "unblock";
    await toggleFoodDelivery(selectedDriver, enabled);
    setShowBlockDialog(false);
    setSelectedDriver(null);
  };

  const handleFilterChange = (filterType, value) => {
    setPage(1); // Reset to first page on filter change
    if (filterType === "status") setStatusFilter(value);
    if (filterType === "city") setCityFilter(value);
  };

  const goToPage = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setPage(newPage);
    }
  };

  return (
    <div data-testid="food-drivers-management">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <Package className="w-6 h-6 text-orange-500" />
            Food Delivery Drivers
          </h2>
          <p className="text-muted-foreground">
            Manage drivers who have enabled Food Delivery service
          </p>
        </div>
        <Button variant="outline" onClick={fetchFoodDrivers} disabled={loading}>
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </Button>
      </div>

      {/* Info Banner */}
      <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mb-4">
        <p className="text-sm text-orange-800">
          <AlertTriangle className="w-4 h-4 inline mr-2" />
          This list shows all bike riders who have enabled Food Delivery in their app. 
          You can block any driver from accepting food delivery orders by toggling the switch.
        </p>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-4 mb-4">
        <div className="relative flex-1 min-w-[250px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Search by name, phone, or referral code..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            className="pl-10"
            data-testid="search-input"
          />
        </div>
        <Select value={statusFilter} onValueChange={(v) => handleFilterChange("status", v)}>
          <SelectTrigger className="w-[150px]" data-testid="status-filter">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="online">Online</SelectItem>
            <SelectItem value="offline">Offline</SelectItem>
            <SelectItem value="approved">Approved</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
          </SelectContent>
        </Select>
        <Select value={cityFilter} onValueChange={(v) => handleFilterChange("city", v)}>
          <SelectTrigger className="w-[150px]" data-testid="city-filter">
            <SelectValue placeholder="City" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Cities</SelectItem>
            <SelectItem value="unassigned">Unassigned</SelectItem>
            {cities.map((city) => (
              <SelectItem key={city.id} value={city.id}>{city.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Results count */}
      <div className="flex items-center justify-between mb-2">
        <p className="text-sm text-muted-foreground">
          {loading ? "Loading..." : `Showing ${drivers.length} of ${total} food delivery drivers`}
        </p>
        {totalPages > 1 && (
          <p className="text-sm text-muted-foreground">
            Page {page} of {totalPages}
          </p>
        )}
      </div>

      {/* Drivers Table */}
      <div className="border rounded-xl overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Driver</TableHead>
              <TableHead>Contact</TableHead>
              <TableHead>Vehicle</TableHead>
              <TableHead>City</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Rating</TableHead>
              <TableHead className="text-center">Food Delivery</TableHead>
              <TableHead className="text-center">Max Orders</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={9} className="text-center py-8">
                  <RefreshCw className="w-6 h-6 animate-spin mx-auto text-muted-foreground" />
                </TableCell>
              </TableRow>
            ) : drivers.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} className="text-center py-8 text-muted-foreground">
                  No food delivery drivers found
                </TableCell>
              </TableRow>
            ) : (
              drivers.map((driver) => (
                <TableRow key={driver.id} data-testid={`driver-row-${driver.id}`}>
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${driver.is_online ? 'bg-green-100' : 'bg-gray-100'}`}>
                        <span className="text-lg">{driver.name?.[0]?.toUpperCase() || '?'}</span>
                      </div>
                      <div>
                        <p className="font-medium">{driver.name}</p>
                        <p className="text-xs text-muted-foreground">{driver.referral_code}</p>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <a 
                      href={`tel:${driver.phone}`}
                      className="flex items-center gap-1 text-sm text-blue-600 hover:underline"
                    >
                      <Phone className="w-3 h-3" />
                      {driver.phone}
                    </a>
                  </TableCell>
                  <TableCell>
                    <p className="text-sm capitalize">{driver.vehicle_type || '-'}</p>
                    <p className="text-xs text-muted-foreground">{driver.vehicle_number || '-'}</p>
                  </TableCell>
                  <TableCell>
                    <span className="flex items-center gap-1 text-sm">
                      <MapPin className="w-3 h-3" />
                      {driver.assigned_city_name || 'Unassigned'}
                    </span>
                  </TableCell>
                  <TableCell>
                    <span className={`px-2 py-1 rounded-full text-xs ${
                      driver.is_online 
                        ? 'bg-green-100 text-green-700' 
                        : driver.approval_status === 'approved'
                        ? 'bg-blue-100 text-blue-700'
                        : 'bg-yellow-100 text-yellow-700'
                    }`}>
                      {driver.is_online ? 'Online' : driver.approval_status || 'Pending'}
                    </span>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1">
                      <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                      <span>{driver.rating?.toFixed(1) || '5.0'}</span>
                    </div>
                  </TableCell>
                  <TableCell className="text-center">
                    <div className="flex flex-col items-center gap-1">
                      {driver.food_delivery_blocked ? (
                        <span className="px-2 py-1 rounded-full text-xs bg-red-100 text-red-700 flex items-center gap-1">
                          <AlertTriangle className="w-3 h-3" />
                          Blocked
                        </span>
                      ) : driver.service_types?.food_delivery ? (
                        <span className="px-2 py-1 rounded-full text-xs bg-green-100 text-green-700">
                          Active
                        </span>
                      ) : (
                        <span className="px-2 py-1 rounded-full text-xs bg-gray-100 text-gray-700">
                          Inactive
                        </span>
                      )}
                      <Switch
                        checked={driver.service_types?.food_delivery === true && !driver.food_delivery_blocked}
                        onCheckedChange={(checked) => {
                          if (checked) {
                            toggleFoodDelivery(driver, true);
                          } else {
                            openBlockDialog(driver, "block");
                          }
                        }}
                        data-testid={`food-delivery-toggle-${driver.id}`}
                      />
                      {driver.service_types?.food_delivery ? (
                        <CheckCircle className="w-4 h-4 text-green-500" />
                      ) : (
                        <Ban className="w-4 h-4 text-red-500" />
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="text-center">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-blue-600 hover:bg-blue-50"
                      onClick={() => openMaxOrdersDialog(driver)}
                      data-testid={`max-orders-btn-${driver.id}`}
                    >
                      {driver.max_parallel_food_orders || 'Default'}
                    </Button>
                  </TableCell>
                  <TableCell>
                    <Button
                      variant="outline"
                      size="sm"
                      className={driver.service_types?.food_delivery 
                        ? "text-red-600 border-red-200 hover:bg-red-50" 
                        : "text-green-600 border-green-200 hover:bg-green-50"}
                      onClick={() => openBlockDialog(driver, driver.service_types?.food_delivery ? "block" : "unblock")}
                      data-testid={`block-btn-${driver.id}`}
                    >
                      {driver.service_types?.food_delivery ? (
                        <>
                          <Ban className="w-3 h-3 mr-1" />
                          Block
                        </>
                      ) : (
                        <>
                          <CheckCircle className="w-3 h-3 mr-1" />
                          Enable
                        </>
                      )}
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-4">
          <p className="text-sm text-muted-foreground">
            Showing {((page - 1) * limit) + 1} - {Math.min(page * limit, total)} of {total}
          </p>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => goToPage(1)}
              disabled={page === 1 || loading}
            >
              <ChevronsLeft className="w-4 h-4" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => goToPage(page - 1)}
              disabled={page === 1 || loading}
            >
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <span className="px-3 py-1 text-sm">
              {page} / {totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => goToPage(page + 1)}
              disabled={page === totalPages || loading}
            >
              <ChevronRight className="w-4 h-4" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => goToPage(totalPages)}
              disabled={page === totalPages || loading}
            >
              <ChevronsRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}

      {/* Block/Unblock Confirmation Dialog */}
      <Dialog open={showBlockDialog} onOpenChange={setShowBlockDialog}>
        <DialogContent data-testid="block-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {blockAction === "block" ? (
                <>
                  <Ban className="w-5 h-5 text-red-500" />
                  Block from Food Delivery
                </>
              ) : (
                <>
                  <CheckCircle className="w-5 h-5 text-green-500" />
                  Enable Food Delivery
                </>
              )}
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            {selectedDriver && (
              <div className="p-4 bg-muted rounded-lg mb-4">
                <p className="font-medium">{selectedDriver.name}</p>
                <p className="text-sm text-muted-foreground">{selectedDriver.phone}</p>
              </div>
            )}
            <p className="text-sm text-muted-foreground">
              {blockAction === "block" 
                ? "This driver will no longer be able to accept food delivery orders. They will only see bike taxi rides."
                : "This driver will be able to accept food delivery orders along with bike taxi rides."}
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBlockDialog(false)}>
              Cancel
            </Button>
            <Button 
              variant={blockAction === "block" ? "destructive" : "default"}
              onClick={handleConfirmBlock}
              data-testid="confirm-block-btn"
            >
              {blockAction === "block" ? "Block Driver" : "Enable Food Delivery"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Max Parallel Orders Dialog */}
      <Dialog open={showMaxOrdersDialog} onOpenChange={setShowMaxOrdersDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Layers className="w-5 h-5 text-blue-500" />
              Max Parallel Orders
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            {selectedDriver && (
              <div className="p-4 bg-muted rounded-lg mb-4">
                <p className="font-medium">{selectedDriver.name}</p>
                <p className="text-sm text-muted-foreground">{selectedDriver.phone}</p>
                <p className="text-sm text-muted-foreground">City: {selectedDriver.assigned_city_name || 'Unassigned'}</p>
              </div>
            )}
            <p className="text-sm text-muted-foreground mb-4">
              Set the maximum number of food delivery orders this rider can accept simultaneously.
              <br /><span className="text-xs">Leave as "Default" to use city setting, or 1 if city has no setting.</span>
            </p>
            <div className="flex flex-col gap-2">
              <label className="text-sm font-medium">Max Parallel Orders</label>
              <select
                value={maxOrdersValue}
                onChange={(e) => setMaxOrdersValue(e.target.value)}
                className="w-full p-2 border rounded-lg"
                data-testid="max-orders-select"
              >
                <option value="">Default (City Setting)</option>
                <option value="1">1 - Single order only</option>
                <option value="2">2 - Two orders</option>
                <option value="3">3 - Three orders</option>
                <option value="4">4 - Four orders</option>
                <option value="5">5 - Five orders</option>
              </select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowMaxOrdersDialog(false)}>
              Cancel
            </Button>
            <Button onClick={updateMaxParallelOrders} data-testid="save-max-orders-btn">
              Save Setting
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FoodDriversManagement;
